import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, User, Search, Wand2, Users, HeartHandshake, Layers } from 'lucide-react';

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ── Logged-out landing ─────────────────────────────────────────── */
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">

        {/* Header */}
        <header className="border-b border-orange-100/60 bg-white/50 backdrop-blur-sm">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#e8744f] to-[#d66440] shadow-md">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Folio</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Portfolio matching platform</p>
              </div>
            </div>
            <Link to="/login">
              <Button
                variant="ghost"
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-4 py-2 text-sm text-gray-700 hover:bg-white transition"
              >
                <User size={15} />
                <span className="hidden sm:inline">Log In</span>
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <main className="mx-auto max-w-6xl px-6 py-14 space-y-20">
          <section className="flex flex-col items-center text-center space-y-6">

            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm">
              <Sparkles className="h-3 w-3 text-[#e8744f]" />
              Auto-portfolio builder · Hinge-style browsing
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight max-w-3xl">
              Build a portfolio people{' '}
              <span className="bg-gradient-to-r from-[#e8744f] to-[#d66440] bg-clip-text text-transparent">
                actually read.
              </span>
            </h2>

            <p className="text-base sm:text-lg text-gray-600 max-w-xl">
              Answer fun prompts → Auto portfolio → Browse others portfolios.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/register">
                <Button
                  className="rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white px-8 py-5 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                  data-testid="hero-start-building-button"
                >
                  Start building
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  variant="outline"
                  className="rounded-full border border-gray-300 bg-white/70 px-8 py-5 text-sm font-medium text-gray-700 hover:bg-white hover:border-gray-400 transition-all duration-200"
                  data-testid="hero-browse-button"
                >
                  Browse portfolios
                </Button>
              </Link>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-4 pt-2 text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5 text-[#e8744f]" />
                No design skills needed
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-[#e8744f]" />
                Shareable profile link
              </div>
              <div className="flex items-center gap-1.5">
                <HeartHandshake className="h-3.5 w-3.5 text-[#e8744f]" />
                Built to feel personal
              </div>
            </div>
          </section>

          {/* How it works */}
          <section className="space-y-8">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Sparkles className="h-4 w-4 text-[#e8744f]" />
              How it works
            </h3>
            <div className="grid gap-5 md:grid-cols-3">
              {[
                {
                  step: '1',
                  title: 'Answer styled prompts',
                  desc: 'We ask about your work, quirks, proudest projects and more — no boring forms.',
                },
                {
                  step: '2',
                  title: 'Auto-generated portfolio',
                  desc: 'Your answers become a clean, responsive portfolio you can share anywhere.',
                },
                {
                  step: '3',
                  title: 'Browse others',
                  desc: 'Scroll through other creators\' portfolios one by one, with like/skip controls.',
                },
              ].map(({ step, title, desc }) => (
                <div
                  key={step}
                  className="rounded-2xl border border-orange-100 bg-white/70 p-5 shadow-sm space-y-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 border border-orange-100">
                    <span className="text-sm font-bold text-[#e8744f]">{step}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="border-t border-orange-100/60 bg-white/30 mt-10">
          <div className="mx-auto max-w-6xl px-6 py-6 text-center">
            <p className="text-sm text-gray-400">
              Ready to make a portfolio that feels like you? ✨
            </p>
            <p className="text-sm text-gray-300">
              Developed By Anuj Kumar
            </p>
          </div>
        </footer>
      </div>
    );
  }

  /* ── Logged-in home ─────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">

      {/* Header */}
      <header className="border-b border-orange-100/60 bg-white/50 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#e8744f] to-[#d66440] shadow-md">
              <Layers className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Folio</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Portfolio matching platform</p>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex items-center gap-2">
            {user.role === 'admin' && (
              <Link to="/admin">
                <Button
                  variant="ghost"
                  className="rounded-full border border-red-100 bg-red-50 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 transition"
                >
                  <span className="hidden sm:inline">Admin Panel</span>
                  <span className="sm:hidden">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/find-people">
              <Button
                variant="ghost"
                className="rounded-full border border-gray-200 bg-white/70 px-4 py-2 text-xs text-gray-700 hover:bg-white transition"
              >
                <Search size={14} className="sm:hidden" />
                <span className="hidden sm:inline">Find People</span>
              </Button>
            </Link>
            <Link to="/matches">
              <Button
                variant="ghost"
                className="rounded-full border border-gray-200 bg-white/70 px-4 py-2 text-xs text-gray-700 hover:bg-white transition"
              >
                Matches
              </Button>
            </Link>
            <Link to="/my-portfolio">
              <Button
                variant="ghost"
                className="rounded-full border border-gray-200 bg-white/70 px-4 py-2 text-xs text-gray-700 hover:bg-white transition flex items-center gap-1.5"
              >
                <User size={14} />
                <span className="hidden sm:inline">My profile</span>
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-6xl px-6 py-14 space-y-20">
        <section className="flex flex-col items-center text-center space-y-6">

          {/* Pill badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm">
            <Sparkles className="h-3 w-3 text-[#e8744f]" />
            Auto-portfolio builder · Hinge-style browsing
          </div>

          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight max-w-3xl">
            Build a portfolio people{' '}
            <span className="bg-gradient-to-r from-[#e8744f] to-[#d66440] bg-clip-text text-transparent">
              actually read.
            </span>
          </h2>

          <p className="text-base sm:text-lg text-gray-600 max-w-xl">
            Answer fun prompts → Auto portfolio → Browse others portfolios.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={() => navigate('/builder')}
              className="rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white px-8 py-5 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              data-testid="start-building-button"
            >
              Start building
            </Button>
            <Button
              onClick={() => navigate('/browse')}
              variant="outline"
              className="rounded-full border border-gray-300 bg-white/70 px-8 py-5 text-sm font-medium text-gray-700 hover:bg-white hover:border-gray-400 transition-all duration-200"
              data-testid="browse-portfolios-button"
            >
              Browse portfolios
            </Button>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-4 pt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <Wand2 className="h-3.5 w-3.5 text-[#e8744f]" />
              No design skills needed
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-[#e8744f]" />
              Shareable profile link
            </div>
            <div className="flex items-center gap-1.5">
              <HeartHandshake className="h-3.5 w-3.5 text-[#e8744f]" />
              Built to feel personal
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="space-y-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Sparkles className="h-4 w-4 text-[#e8744f]" />
            How it works
          </h3>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              {
                step: '1',
                title: 'Answer styled prompts',
                desc: 'We ask about your work, quirks, proudest projects and more — no boring forms.',
              },
              {
                step: '2',
                title: 'Auto-generated portfolio',
                desc: 'Your answers become a clean, responsive portfolio you can share anywhere.',
              },
              {
                step: '3',
                title: 'Browse others',
                desc: 'Scroll through other creators\' portfolios one by one, with like/skip controls.',
              },
            ].map(({ step, title, desc }) => (
              <div
                key={step}
                className="rounded-2xl border border-orange-100 bg-white/70 p-5 shadow-sm space-y-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 border border-orange-100">
                  <span className="text-sm font-bold text-[#e8744f]">{step}</span>
                </div>
                <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-orange-100/60 bg-white/30 mt-10">
        <div className="mx-auto max-w-6xl px-6 py-6 text-center">
          <p className="text-sm text-gray-400">
            Ready to make a portfolio that feels like you? ✨
          </p>
          <p className="text-sm text-gray-300">
            Developed By Anuj Kumar
          </p>
        </div>
      </footer>
    </div>
  );
}